// File: data-table.component.ts
// Author: Excel Ortega
// Date: 4/27/2020
// Desc: Base component for data table.
// Other Contributors: Patrick Wegner, Mason Beckham

import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef } from '@angular/core';
import { LazyLoadEvent } from 'primeng/api/primeng-api';
import { ColumnDef, DataTableValidator } from './data-table.interface';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Platform } from '@angular/cdk/platform';
import { defaultIfEmpty, filter, first, flatMap, map, take, tap } from 'rxjs/operators';

@Component({
   // tslint:disable-next-line: component-selector
   selector: 'data-table',
   templateUrl: './data-table.component.html',
   styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent implements OnInit {

   readonly FILTER_COLUMN_DISPLAY_NAME = 'Filter Column Display';
   // Default values for the # of items seen on the grid at a time.
   ROWCOUNT_OPTIONS: any[] = this.platform.TRIDENT ?
      [10, 20, 50] : // IE
      [50, 100, 300]; // Others

   // The string here is a format that primeng paginator expects
   readonly PAGE_REPORT = '{first} to {last} of {totalRecords} items';

   private _data: any[];
   /**
    * The data that's been selected in the grid. Single item selection
    * is an array with the item in the first index.
    * 
    * @private
    * @type {any[]}
    * @memberof DataTableComponent
    */
   private _selections: any[] = [];
   private _columns: ColumnDef[];
   private _isShowFilter = false;
   private _sortField = '';
   selected;
   displayedColumns: ColumnDef[];
   displayableColumns: ColumnDef[];
   focusedValues: any[] = [];
   inputErrors: string[] = [];
   hoverInfo: { title: string, data: string }[] = [];

   @ViewChild('erroroverlay') errorOverlay: OverlayPanel;
   @ViewChild('hoveroverlay') hoverOverlay: OverlayPanel;

   @Input() pageSize = 5;
   @Input() loading = false;
   @Input() lazy = false;
   @Input() paging = false;
   @Input() filterColumnDisplay = true;
   @Input() totalRecords = 0;
   @Input() lazyLoadOnInit = true;
   @Input() showCurrentPageReport = true;

   /**
    * Enables the column that display row numbers.
    * 
    * @memberof DataTableComponent
    */
   @Input() showNumberColumn = true;
   /**
    * Enables the paging option to show all
    * elements.
    *
    * @memberof DataTableComponent
    */
   @Input() showAllPaging = false;
   /**
    * Enables the table filter functionality. 
    * 
    * Enabled filter functionality will display a 
    * toggleable filter button.
    *
    * @memberof DataTableComponent
    */
   @Input() filterable = false;

   /**
    * Enables table multi-selection. Prepends a column with checkboxes
    * for multi-selection in the table body.
    *
    * @memberof DataTableComponent
    */
   @Input() multiselect = false;

   /**
   * Enables tooltips for multi-select checkboxes.
   *
   * @memberof DataTableComponent
   */
   @Input() multiselectTooltip: string;

   /**
 * Should add an empty selection field
 *
 * @type {boolean}
 * @memberof DataTableComponent
 */
   @Input() emptySelection = false;

   @Input() trackByField: string;

   @Output() sort = new EventEmitter();

   /**
    * Emits the row selected as an object. Columns are properties.
    *
    * @memberof DataTableComponent
    */
   @Output() unSelect = new EventEmitter();
   @Output() select = new EventEmitter();
   @Output() edit = new EventEmitter();
   @Output() lazyLoad = new EventEmitter();
   @Output() onPage = new EventEmitter();
   @Output() onEditClick = new EventEmitter();
   @Output() onEditSave = new EventEmitter();
   @Output() onSelectionFilter = new EventEmitter();

   @Input() canSelectRow: (selection: any) => boolean = () => true;

   @Input('defaultSortField')
   set sortField(sortfield: string) {
      if (!this._sortField && sortfield && sortfield.length > 0) {
         this._sortField = sortfield;
      }
   }

   get sortField(): string {

      if (this._sortField && this._sortField.length > 0) {
         return this._sortField;
      } else if (this._columns && this._columns.length > 0) {
         return this._columns[0].field;
      }

      return null;
   }

   get data(): any[] {
      return this._data;
   }

   @Input('data')
   set data(collection: any[]) {
      if (collection) {
         this._data = collection;
      } else {
         this._data = [];
      }
   }

   get columns(): ColumnDef[] {
      if (this._columns) {
         return this._columns;
      } else {
         return [];
      }
   }

   set selections(value: any[]) {
      this._selections = ((value instanceof Array) ?
         value.filter(el => Object.keys(el).length > 0) :
         [value]
      ).filter(selection => !this.canSelectRow || this.canSelectRow(selection));

      if (this._selections.length !== (value || []).length) {
         this.onSelectionFilter.emit(this._selections);
      }
   }

   get selections(): any[] {
      return this._selections.slice();
   }

   @Input('columns')
   set columns(collection: ColumnDef[]) {
      if (collection && collection.length > 0) {
         this._columns = this.sanitizeColumnValues(collection);
         this.displayableColumns = this.filterDisplayableColumns(this._columns);
         this.displayedColumns = this.displayableColumns;
      }
   }

   get filterDisplayed(): boolean {
      return this._isShowFilter;
   }

   /**
    * The number of columns that are displayed.
    *
    * @readonly
    * @type {number}
    * @memberof DataTableComponent
    */
   get displayedColumnLength(): number {
      let total = 0;
      // Enabling multiselect adds a checkbox column
      // so we add 1 here
      if (this.multiselect) { total += 1; }

      // Enabling the number column adds a column for
      // row index so we add 1 here
      if (this.showNumberColumn) { total += 1; }

      // Count the number of columns that are visible and displayed
      total += this.columns.reduce((acc, curr, idx, arr) => {
         let ct = 0;
         if ((!curr.canEditDisplay && curr.visible) || this.displayedColumns.find(el => el.field === curr.field)) {
            ct = 1;
         }
         return acc + ct;
      }, 0);

      return total;
   }

   /**
    * Flag to check if there is no data in the table.
    *
    * @readonly
    * @type {boolean}
    * @memberof DataTableComponent
    */
   get isEmpty(): boolean {
      if (this.data === undefined) {
         return true;
      }
      else {
         return this.data.length === 0;
      }
   }

   /**
    * Flag to check if the paging component is displayed.
    */
   get paged(): boolean {
      return this.paging && this.data !== undefined;
   }

   constructor(
      private readonly platform: Platform
   ) { }

   ngOnInit() {
      if (this.showAllPaging) {
         this.ROWCOUNT_OPTIONS.push({ showAll: 'All' });
      }

      this.columns
         .filter(col => col.type === 'dropdown' && col.isEmptySelection)
         .forEach(col => {
            this.optionsList(col.optionsList, col)
               .subscribe(options => options.unshift({ [col.optionsListField]: null, [col.optionsListName]: '' }));
         });
   }

   /**
    * Set default values for a `ColumnDef` collection.
    *
    * @private
    * @param {ColumnDef[]} columns
    * @returns
    * @memberof DataTableComponent
    */
   private sanitizeColumnValues(columns: ColumnDef[]) {
      const columnDefaults = {
         canEditDisplay: true,
         width: 0,
         isEmptySelection: false,
         editable: true,
         visible: true,
         filterField: true,
         greyedOut: true,
         headerHighlight: true,
         cellEditable: () => true
      };

      return columns.map(el => {
         el.name = el.name.trim();
         Object.entries(columnDefaults)
            .forEach(([key, value]) => el[key] = el.hasOwnProperty(key) ? el[key] : value);
         return el;
      });
   }

   public getTooltipText(tooltip: string | Function, data: any) {
      return tooltip instanceof Function ? tooltip(data) : tooltip;
   }

   public getTooltipOverflowText(data: string, element: any) {
      return data && element.offsetWidth < element.scrollWidth ? data + '\n' : '';
   }

   private filterDisplayableColumns(cols: ColumnDef[]): ColumnDef[] {
      return cols.filter(el => el.canEditDisplay && el.visible);
   }

   onLazyLoad = (event: LazyLoadEvent) => {
      this.focusedValues = [];
      this.lazyLoad.emit(event);
   }

   isColumnDisplayed(field: string): boolean {
      const col = this.columns.find(el => el.field === field);
      if (!col) { return false; }

      return col.visible && (!col.canEditDisplay
         || this.displayedColumns.some(el => el.field === field));
   }

   isSorted(field: string): boolean {
      return this.sortField === field;
   }

   toggleFilterDisplay = () => {
      this._isShowFilter = !this._isShowFilter;
   }

   onPageChange = page => {
      // Whenever a page change event occurs, reset selected row
      this.selected = null;
      this.select.emit(null);

      this.selections = [];

      // Emit page change event
      this.onPage.emit(page);
   }

   onSelect = selected => {
      this.selected = { ...selected.data };
      if (Object.keys(this.selected).length !== 0) {
         this.select.emit(this.selected);
      }
   }

   onUnselect = (selected) => {
      this.select.emit(null);
      this.unSelect.emit(selected.data);
   }

   storeFocusedValue = (row, data) => {
      this.focusedValues[row] = data;
   }

   onFocusInput = (el: { input: HTMLInputElement, rowIndex: number, data: any }) => {
      // Ensures that the cursor is set in the beginning
      // of the input and doesn't scroll to the end.
      // Used for cosmetic purpose
      if (el.input) {
         el.input.scrollLeft = 0;
      }
      this.storeFocusedValue(el.rowIndex, el.data);
   }

   onEdit(dataItem: any, field: string, rowIndex: number) {
      this.errorOverlay.hide();

      if (dataItem[field] !== this.focusedValues[rowIndex] || (this.selected && dataItem[field] !== this.selected[field])) {

         const column = this.columns.find(col => col.field === field);

         // Verify all validators
         const validators = column.validators || [];
         const failed = (validators instanceof Array ? validators : [validators])
            .filter((validator: DataTableValidator) => !validator.validator(dataItem[field], dataItem));

         // If data was not valid, reset field
         if (failed.length >= 1) {
            setTimeout(() => dataItem[field] = this.focusedValues[rowIndex] || this.selected[field]);
            failed
               .filter((validator: DataTableValidator) => !!validator.invalid)
               .forEach((validator: DataTableValidator) => validator.invalid(column));
            return;
         } else {
            this.edit.emit({
               data: dataItem,
               field,
               rowIndex
            });
         }

      }
   }

   onSort = sorted => {
      this._sortField = sorted.field;
      this.sort.emit(sorted);
   }

   validateFieldInput = (input, col: ColumnDef) => {
      const validators = col.validators || [];
      this.inputErrors = (validators instanceof Array ? validators : [validators])
         .filter((validator: DataTableValidator) => !validator.validator(input.target.value))
         .map(validator => validator.snippit || 'Invalid input for field');

      if (this.inputErrors.length > 0) {
         this.errorOverlay.show(input, input.target);
      } else {
         this.inputErrors = [];
         this.errorOverlay.hide();
      }
   }

   getDropdownValue(field, colField, colName, options, placeholder) {
      return Array.isArray(options) ?
         (options.find(option => option[colField] === field) || {})[colName] || placeholder :
         placeholder;
   }

   trackBy(field: string) {
      return (index: number, data: any) => field ? data[field] : data;
   }

   /** HELPER FUNCTIONS */

   /**
    * Guarantees that the data for an options list
    * is subscribeable.
    * 
    * This is needed because the user can populate the options
    * list data asynchronously.
    *
    * @param {(any[] | Observable<any[]>)} data
    * @returns {Observable<any>}
    * @memberof DataTableComponent
    */
   optionsList(data: any[] | Observable<any[]>, col: ColumnDef): Observable<any> {
      return Array.isArray(data) ? of(data) : data;
   }

   cleanDate(date) {
      if (date) {
         return moment(date).format('MM/DD/YYYY');
      }
      return '';
   }

   handleHoverEnter(event: any, data: any, callback: (data: any) => { title: string, data: string }[] = () => []) {
      this.hoverInfo = callback(data);
      if (this.hoverInfo && this.hoverInfo.length > 0) {
         this.hoverOverlay.show(event, event.target);
      }
   }

   handleHoverLeave() {
      this.hoverOverlay.hide();
   }

   handleTooltipEnter(event: any, data: any) {
      const element = event.target;
      if (data && element.offsetWidth < element.scrollWidth) {
         event.target.setAttribute('title', data);
      }
   }

   handleTooltipLeave(event: any) {
      event.target.removeAttribute('title');
   }

   clearSelection() {
      this.selected = null;
   }
}
